import { GridApi, GridLeafColumn } from '@mui/x-data-grid-pro';
import { CuriosityNamespace, UseCuriosityTranslationLoaderResult } from '@procureai/curiosity-ui-mui';
import { format } from 'date-fns';
import * as Excel from 'exceljs';
import saveAs from 'file-saver';
import { clone } from 'ramda';
import { MutableRefObject } from 'react';

import { DataGridConfig } from '../useDataGridConfig/useDataGridConfig.types';

export const DEFAULT_COLUMN_WIDTH = 150;

export const exportHeader = (worksheet: Excel.Worksheet, config: DataGridConfig) => {
  const { columns } = config;

  const cols = columns.reduce((acc, { field, width }) => {
    const exportModel = config.exportModel?.[field];

    if (!exportModel?.disable) {
      acc.push({
        width: (width || DEFAULT_COLUMN_WIDTH) / 8,
        key: field,
      });
    }
    return acc;
  }, [] as Partial<Excel.Column>[]);

  worksheet.columns = cols;

  columns.forEach(({ field }) => {
    const exportModel = config.exportModel?.[field];
    if (exportModel?.numFmt) {
      worksheet.getColumn(field).numFmt = exportModel?.numFmt;
    }
  });

  if (config.columnGroupingModel) {
    const bandsRow = config.columnGroupingModel.reduce(
      (acc, { headerName, children }) => ({
        ...acc,
        [(children?.[0] as GridLeafColumn)?.field || '']: headerName,
      }),
      {},
    );
    worksheet.addRow(bandsRow);
  }

  const headerRow = columns.reduce(
    (acc, { field, headerName }) => ({
      ...acc,
      [field]: headerName,
    }),
    {},
  );
  worksheet.addRow(headerRow);

  worksheet.views.push({
    state: 'frozen',
    ySplit: worksheet.lastRow?.number || 0,
  });
};

interface ResolveCuriosityFieldProps {
  value: any;
  ns: CuriosityNamespace;
  curiosityLoader: UseCuriosityTranslationLoaderResult;
}

const resolveCuriosityField = async ({ value, ns, curiosityLoader }: ResolveCuriosityFieldProps) => {
  const loadSingleValue = async (key: string) => {
    try {
      const translation = await curiosityLoader.load(ns, key);
      return translation?.value;
    } catch (e) {
      /* ignore error if translation for key not found */
    }
    return null;
  };
  return Array.isArray(value) ? Promise.all(value.map(loadSingleValue)) : loadSingleValue(value);
};

interface PrepareDataProps {
  data: any[];
  config: DataGridConfig;
  curiosityLoader: UseCuriosityTranslationLoaderResult;
  apiRef: MutableRefObject<GridApi>;
}

const prepareData = async ({ data, curiosityLoader, config, apiRef }: PrepareDataProps) => {
  const promises: Promise<any>[] = [];

  // using promises inside instead of async/await on purpose, as I want to
  // synchronously trigger the loading of curiosity translations (to be able
  // to make use of automatic request batching)
  const result = clone(data).map((row: any) => {
    return config.columns.reduce(
      (acc, col) => {
        const colDef = apiRef.current.getColumn(col.field);
        const exportModel = config.exportModel?.[colDef.field];
        if (!exportModel?.disable) {
          const rawValue = row[colDef.field];
          // get value from row
          let promise = Promise.resolve(
            colDef.valueGetter ? colDef.valueGetter(rawValue as never, row, colDef, apiRef) : rawValue,
          );
          // resolve curiosity value
          const ns = exportModel?.resolveCuriosity;
          if (ns) {
            promise = promise.then((value) => resolveCuriosityField({ value, ns, curiosityLoader }));
          }
          // apply value formatter
          promise.then((value) => {
            acc[colDef.field] = colDef.valueFormatter
              ? colDef.valueFormatter(value as never, row, colDef, apiRef)
              : value;
          });
          promises.push(promise);
        }
        return acc;
      },
      {} as { [key: string]: any },
    );
  });

  // wait for all promises to resolve, otherwise the data is not updated yet
  await Promise.all(promises);
  return result;
};

interface ExportRowsProps {
  data: any[];
  worksheet: Excel.Worksheet;
}

export const exportRows = ({ worksheet, data }: ExportRowsProps) => {
  data.forEach((row) => {
    worksheet.addRow(row);
  });
};

interface ExportGridDataProps {
  data: any[];
  config: DataGridConfig;
  fileName: string;
  curiosityLoader: UseCuriosityTranslationLoaderResult;
  apiRef: MutableRefObject<GridApi>;
}

export const exportGridData = async ({ data, config, fileName, curiosityLoader, apiRef }: ExportGridDataProps) => {
  const now = new Date();
  const workbook = new Excel.Workbook();
  const worksheet = workbook.addWorksheet('Main');

  exportHeader(worksheet, config);
  const dataPrepared = await prepareData({
    data,
    config,
    curiosityLoader,
    apiRef,
  });

  exportRows({ worksheet, data: dataPrepared });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${format(now, 'yyyyMMddHHmm')}_${fileName}.xlsx`);
};
